import {
  Droplets, Landmark, ShieldCheck, Layers, Building2, Columns,
  DoorOpen, ArrowUpFromLine, Home, Zap, Wrench, Flame,
  PaintBucket, Armchair, Hammer, Trash2, HardHat,
  Grid3X3, Fan, Package, Scaling, Cuboid,
  Truck, Scale, Construction, Fence, Shield,
  type LucideIcon,
} from "lucide-react";

// Icon per summary sub-group (mirrors SUB_GROUP_LUCIDE_MAP in pdf-generator.ts
// so the on-screen summary view matches the Summary PDF badges).
export const SUB_GROUP_ICONS: Record<string, LucideIcon> = {
  demolition_works: Trash2,
  ground_works: Droplets,
  masonry_works: Building2,
  structural_steel: Columns,
  thermal_insulation: Grid3X3,
  pitched_roofing: Home,
  flat_roofing: Home,
  roof_windows: DoorOpen,
  fascias_guttering: Home,
  windows_doors: DoorOpen,
  first_fix_electrics: Zap,
  second_fix_electrics: Zap,
  first_fix_plumbing: Wrench,
  second_fix_plumbing: Wrench,
  heating: Flame,
  drylining_works: Layers,
  second_fix_joinery: Hammer,
  making_good: Package,
  decorative_finishes: PaintBucket,
};

const CATEGORY_ICON_MAP: Record<string, LucideIcon> = {
  // Groundworks & substructure
  groundworks: Droplets,
  foundations: Landmark,
  drainage: Droplets,
  damp_proofing: ShieldCheck,
  // Masonry
  brickwork: Building2,
  blockwork: Building2,
  internal_walls: Layers,
  // Structural steel
  steel: Columns,
  steel_heavy: Columns,
  steel_medium: Columns,
  steel_post: Columns,
  lintel: Columns,
  padstone: Landmark,
  fire_protection: Flame,
  propping: Construction,
  temporary_works: Construction,
  // Carpentry & stud walls
  carpentry: Hammer,
  stud_walls: Layers,
  // Roofing
  roofing: Home,
  flat_roofing: Home,
  roof_repair: Home,
  fascias_soffits: Home,
  // Windows & doors
  windows_doors: DoorOpen,
  windows: DoorOpen,
  bifold_doors: DoorOpen,
  external_doors: DoorOpen,
  // Services
  insulation: Grid3X3,
  electrics: Zap,
  electrical: Zap,
  plumbing: Droplets,
  pipework: Droplets,
  ventilation: Fan,
  heating: Flame,
  // Internal finishes
  plastering: PaintBucket,
  render: PaintBucket,
  tiling: Cuboid,
  flooring: Scaling,
  decorating: PaintBucket,
  // Fit-out
  kitchens: Armchair,
  bathrooms: Droplets,
  bathroom_fitting: Droplets,
  // Preliminaries & site
  preliminaries: HardHat,
  scaffolding: ArrowUpFromLine,
  waste: Trash2,
  skip: Trash2,
  demolition: Trash2,
  plant: Truck,
  making_good: Package,
  external_works: Droplets,
  // Professional & legal
  legal: Scale,
  legal_fees: Scale,
  structural_engineering: Scale,
  // Misc
  heating_ventilation: Fan,
  equipment_hire: Truck,
  site_equipment: Construction,
  fences_barriers: Fence,
  preparation_protection: Shield,
  wall_formwork: Landmark,
  formwork: Landmark,
  hoarding: Fence,
  protection: Shield,
};

// Full 35-position construction sequence — mirrors the backend engine's
// CONSTRUCTION_SEQUENCE map so the frontend sort order matches realistic
// construction phasing across all scopes in multi-scope quotes.
const CONSTRUCTION_SEQUENCE_FRONTEND: Record<string, number> = {
  // ── Prelims & fees ──────────────────────────────────────────────
  legal: 1, legal_fees: 1, structural_engineering: 1,
  preliminaries: 2, site_setup: 2,
  propping: 3, temporary_works: 3,
  plant: 4,
  scaffolding: 5,
  waste: 6, skip: 6,
  demolition: 7,
  // ── Groundworks & substructure ───────────────────────────────────
  groundworks: 8,
  drainage: 9,
  foundations: 10,
  damp_proofing: 11,
  // ── Superstructure ───────────────────────────────────────────────
  brickwork: 12, blockwork: 12,
  internal_walls: 13,
  steel: 14, steel_post: 14, steel_heavy: 14, steel_medium: 14,
  lintel: 14, padstone: 14, fire_protection: 14,
  carpentry: 15,       // 1st fix only — second-fix overridden by name below
  stud_walls: 16,
  // ── Roofing ──────────────────────────────────────────────────────
  roofing: 17, flat_roofing: 17, roof_repair: 17, fascias_soffits: 17,
  // ── Windows & external doors ─────────────────────────────────────
  windows_doors: 18, windows: 18,
  bifold_doors: 19, external_doors: 19,
  // ── Services ─────────────────────────────────────────────────────
  insulation: 20,
  electrics: 21, electrical: 21,
  plumbing: 22,
  ventilation: 23, heating: 23, heating_ventilation: 23,
  // ── Internal finishes ────────────────────────────────────────────
  plastering: 24,      // plasterboard, dot & dab, skim — before any 2nd fix
  render: 25,
  tiling: 26,
  flooring: 27,
  second_fix_carpentry: 28,  // skirtings, architraves, window boards, door hanging
  bathroom_fitting: 29,      // sanitaryware, shower screens — after tiling & 2nd fix
  decorating: 30, internal_finishes: 30, finishes: 30,
  kitchens: 31,
  bathrooms: 32,
  // ── External & completion ────────────────────────────────────────
  external_works: 33,
  making_good: 34,
};

// Items the AI files under category "carpentry" (pos 15) but that are
// genuinely second-fix work — must sort after plastering and flooring (pos 28).
const SECOND_FIX_CARPENTRY_NAMES_FRONTEND = [
  "skirting board", "skirting boards", "architrave", "architrave installation",
  "window board", "window sill",
  "internal door installation", "door installation", "door hanging",
];

// Bathroom/shower tanking is categorised as "damp_proofing" (pos 11) by the AI,
// but in practice it is applied immediately before tiling — so it belongs at
// pos 25 (between plastering 24 and tiling 26).
const BATHROOM_TANKING_NAMES_FRONTEND = [
  "waterproofing / tanking", "waterproofing/tanking",
  "tanking", "bathroom tanking", "shower tanking", "wet room tanking",
  "bathroom waterproofing", "shower waterproofing", "wet room waterproofing",
  "tanking system", "tanking membrane", "waterproof membrane",
  "liquid waterproofing",
];

export function getItemSortPriority(category: string, itemName?: string): number {
  const cat = (category || "").toLowerCase().replace(/[\s&-]+/g, "_").replace(/_+/g, "_");
  const name = (itemName || "").toLowerCase();

  // Name-based second-fix override — must come before the category lookup so
  // that items filed under "carpentry" (pos 15) still sort at pos 28.
  if (SECOND_FIX_CARPENTRY_NAMES_FRONTEND.some(n => name.includes(n))) return 28;

  // Bathroom tanking override — damp_proofing (pos 11) by category, but
  // construction-sequence-wise it belongs between plastering (24) and tiling (26).
  if (cat === "damp_proofing" && BATHROOM_TANKING_NAMES_FRONTEND.some(n => name.includes(n))) return 25;

  return CONSTRUCTION_SEQUENCE_FRONTEND[cat] ?? 50;
}

export function getCategoryIcon(category: string): LucideIcon | null {
  const key = category.toLowerCase().replace(/[\s&-]+/g, "_").replace(/_+/g, "_");
  if (CATEGORY_ICON_MAP[key]) return CATEGORY_ICON_MAP[key];

  for (const [mapKey, icon] of Object.entries(CATEGORY_ICON_MAP)) {
    if (key.includes(mapKey) || mapKey.includes(key)) return icon;
  }

  return null;
}
